import { Box } from '@mui/material'
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined'
import { HierarchyNode } from './HierarchyNode.tsx'
import { memo, useContext, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { explorerNodesConfig } from './ExplorerNodesConfig.tsx'
import { CollectionTreeContext } from './ICollectionTreeContext.tsx'
import type { NodeOptionPopoverItem } from './HierarchyNodeOptionsPopoverItemsProps.tsx'
import { useDiRef } from '@/common/react/di-hooks'
import type { CollectionsTreeService } from '../services/CollectionsTreeService'
import { RenamableNodeName } from './HierarchyNodeName.tsx'
import { PopoverOptionsMenuIcon } from '@/core/popover/options/PopoverOptionsMenuIcon.tsx'
import type { CollectionModel } from '@/core/gql/gql-models-override.ts'

const config = explorerNodesConfig

export const CollectionExplorerNodeMemo = memo(
  HierarchyNode,
  (prev, next) => prev.subtreeVersion === next.subtreeVersion,
)

type CollectionNodeProps = {
  level: number
  collection: CollectionModel
}

export function CollectionNode({ level, collection }: CollectionNodeProps) {
  const { optionPopoverHooks, onCollectionClicked } = useContext(CollectionTreeContext)!
  const location = useLocation()
  const isActive = new URLSearchParams(location.search).get('id') === collection.id
  const collectionsService = useDiRef<CollectionsTreeService>('CollectionsTreeService')
  const [isRenaming, setIsRenaming] = useState(false)
  function onPopoverOptionsSelected(option: NodeOptionPopoverItem) {
    switch (option) {
      case 'Rename': {
        setIsRenaming(true)
        break
      }
      case 'Delete': {
        void collectionsService.deleteCollection(collection.id)
        break
      }
    }
  }

  function onRenameSubmit(name: string) {
    void collectionsService.updateCollection({ id: collection.id, name })
  }

  const nodeContent = (
    <Box
      sx={{
        justifyContent: 'flex-start',
        ...config.nodeSx,
        marginLeft: level === 0 ? 0 : config.levelIndent,
        ...(isActive && { backgroundColor: 'action.selected' }),
        '@media (hover: none)': { userSelect: 'none' },
      }}
      onClick={() => onCollectionClicked(collection)}
      className="sidebarMenuListItem"
    >
      <CollectionsOutlinedIcon sx={config.iconSx} />

      <RenamableNodeName
        {...{ isRenaming, setIsRenaming, onRenameSubmit, initialName: collection.name }}
      />

      <PopoverOptionsMenuIcon
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.stopPropagation()
          optionPopoverHooks.openPopover(e.currentTarget, onPopoverOptionsSelected)
        }}
      />
    </Box>
  )

  return nodeContent
}
